import * as PropTypes from 'prop-types';
import * as React from 'react';

import { HonestWork } from '@cian/honestwork-component';
import { OwnerBadge } from '@cian/owner-badge-component';
import { PopupArrow } from '@cian/popup-component';
import { mergeStyles } from '@cian/utils';

import { PartnerTooltip } from '../../components/PartnerTooltip';
import { ShowMoreOffers } from '../../components/showMoreOffers';
import { UserInfoBrandPopup } from '../../common/components/UserInfoBrandPopup';
import { IOfferHelper } from '../../presenter';
import { EUserTrustLevel, IOffer } from '../../types/offer';
import { IUser } from '../../types/user';
import { getIsFromBuilderFromContext } from '../../utils/suburban';
import { ContactStatus } from '../../common/components/ContactStatus';
import { getNewbuildingLabel } from '../../utils/getNewbuildingLabel';
import { INewbuildingLabel } from '../../types/newbuildingLabel';

import * as styles from './HorizontalAgentBrand.css';

export interface IAgentBrandContext {
  offer: IOffer;
  user: IUser | null;
  offerHelper: IOfferHelper;
  countrysideOffersBuilderBadge: boolean;
  newbuildingLabel: INewbuildingLabel | null;
  onlineShowEnabled: boolean;
}

/**
 * Компонент информации об агентстве для узкой версии царь-карточки.
 *
 * Рендерится как горизонтальный блок под галереей начиная с разрешения 1240px по ширине (и меньше).
 * Имеет существенные структурные отличия от основного блока (AgentBrand), поэтому не наследуется от него.
 */
export class HorizontalAgentBrand extends React.Component {
  public context: IAgentBrandContext;

  public static contextTypes = {
    offer: PropTypes.object,
    user: PropTypes.object,
    offerHelper: PropTypes.object,
    countrysideOffersBuilderBadge: PropTypes.bool,
    newbuildingLabel: PropTypes.object,
    onlineShowEnabled: PropTypes.bool,
  };

  public render() {
    return (
      <div className={styles['agent']} onClick={this.handleClickWithStopPropagation}>
        {this.renderContactLink()}

        <UserInfoBrandPopup
          isReverseOrder
          content={this.renderBlock()}
          userInfoStyle={styles['user-info']}
          userInfoStyleAction={styles['user-info-action']}
          popupArrowDirection={PopupArrow.LEFT}
          popupStyle={styles['popup']}
        />
      </div>
    );
  }

  private renderContactLink() {
    const { offer, onlineShowEnabled } = this.context;
    const isLeadFactory = this.context.offerHelper.isLeadFactory();
    const isFromDeveloper = this.context.offerHelper.isFromDeveloper();
    const newbuildingId = Number(offer.newbuilding?.id);
    const canShowOnline = onlineShowEnabled && Boolean(offer.user && offer.user.canShowOnline);

    const builderStatusTexts =
      newbuildingId
        ? getNewbuildingLabel(this.context, isFromDeveloper, isLeadFactory, newbuildingId)
        : '';
    if (!builderStatusTexts && !canShowOnline) {
      return null;
    }

    if (builderStatusTexts) {
      return (
        <ContactStatus
          {...builderStatusTexts}
        />
      );
    }

    return (
      <ContactStatus
        labelText="Онлайн-показ"
        tooltipTitle="Выбирайте не выходя из дома"
        tooltipText="Вам могут провести онлайн-показ объекта при помощи видеозвонка в WhatsApp, Viber, FaceTime или другой программы"
      />
    );
  }

  private renderBlock() {
    const userTrustLevel = this.context.offerHelper.presenters.getUserTrustLevel();
    const companyName = this.context.offerHelper.presenters.getCompanyName();
    const isFromBuilder = getIsFromBuilderFromContext(this.context);
    const isFromSeller = this.context.offerHelper.isFromSeller();
    const isLeadFactory = this.context.offerHelper.isLeadFactory();
    const isHomeownerVerified = this.context.offerHelper.isHomeownerVerified();
    const verifyWay = this.context.offerHelper.getVerifyWay();
    const { offer, user } = this.context;
    const { isTop3, user: offerUser, isPremium, isPro, isByHomeowner } = offer;
    const experienceString = this.context.offerHelper.presenters.getExperienceString();

    const masterAgent = offerUser ? offerUser.masterAgent : null;
    const name = this.context.offerHelper.presenters.getDisplayName();
    const agentAvatarUrl = this.context.offerHelper.presenters.getAvatarUrl();

    const isAgentUser = !!user && user.isAgent || false;
    const isCianPartner = !!(offer.user && offer.user.isCianPartner);

    return (
      <div className={styles['main-info']}>
        {(isTop3 || isPremium) && agentAvatarUrl
          ? <img className={styles['avatar']} src={agentAvatarUrl} />
          : null
        }

        <div className={styles['main_info-data']}>
          <div className={styles['name']}>
            <span className={styles['name_wrapper']}>{name}</span>
            <div className={styles['badge-container']}>
              {userTrustLevel === EUserTrustLevel.Involved &&
                <div onClick={this.handleClickWithStopPropagation} className={styles['badge']}>
                  <HonestWork direction="right"/>
                </div>
              }

              {isFromBuilder && !isLeadFactory &&
                <div
                  {...mergeStyles([
                    styles['badge'],
                    styles['text-badge'],
                    styles['green'],
                  ])}
                >
                  Застройщик
                </div>
              }

              {!isFromBuilder && isFromSeller && !isLeadFactory &&
                <div
                  {...mergeStyles([
                    styles['badge'],
                    styles['text-badge'],
                    styles['green'],
                  ])}
                >
                  Представитель<br/>
                  застройщика
                </div>
              }

              {isAgentUser && isPro && !(isFromBuilder || isFromSeller) &&
                <div
                  {...mergeStyles([
                    styles['badge'],
                    styles['text-badge'],
                  ])}
                >
                  PRO
                </div>
              }

              {isByHomeowner &&
                <OwnerBadge isVerified={isHomeownerVerified} verifyWay={verifyWay} />
              }

              {isCianPartner && (
                <PartnerTooltip
                  master={<div {...mergeStyles([styles['badge'], styles['text-badge'], styles['green']])}>Партнёр</div>}
                />
              )}
            </div>
          </div>

          {companyName && !masterAgent &&
            <div className={styles['company-name']}>
              {companyName}
            </div>
          }

          {experienceString &&
            <div className={styles['experience']}>
              {experienceString}
            </div>
          }

          {offer.isTop3 && (
            <div onClick={this.handleClickWithStopPropagation}>
              <ShowMoreOffers/>
            </div>
            )
          }
        </div>
      </div>
    );
  }

  private handleClickWithStopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }
}
